import { WebStorageStateStore, type UserManagerSettings } from "oidc-client-ts";

/**
 * Keycloak OIDC configuration, replacing the Entra External ID / MSAL setup.
 *
 * Keycloak is a standards-compliant OIDC provider, so `authority` is the realm URL and
 * every endpoint is discovered from {authority}/.well-known/openid-configuration. The
 * previous MSAL config had to hand-build a ciamlogin.com authority from the tenant name
 * and declare `knownAuthorities` to stop MSAL rejecting it.
 */
export const oidcConfig: UserManagerSettings & { onSigninCallback?: () => void } = {
  authority: import.meta.env.VITE_OIDC_AUTHORITY as string,
  client_id: import.meta.env.VITE_OIDC_CLIENT_ID as string,
  redirect_uri: window.location.origin,
  post_logout_redirect_uri: window.location.origin,

  // Authorization Code + PKCE. `transitdeal-web` is a public client, so there is no
  // secret and PKCE is what protects the code exchange.
  response_type: "code",
  scope: "openid profile email offline_access",

  // sessionStorage, matching MSAL's previous cacheLocation. Tokens are cleared when the
  // tab closes, and nothing is written to localStorage where an XSS could reach it
  // across sessions.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),

  // Silent renew via the refresh token rather than a hidden iframe: third-party cookie
  // restrictions in modern browsers break iframe-based renewal on a cross-origin
  // provider. Keycloak is same-origin now (/auth on this host), but refresh-token
  // renewal remains the more robust choice and costs nothing.
  automaticSilentRenew: true,
  monitorSession: false,

  /**
   * Strip the ?code=&state= querystring after a successful sign-in.
   *
   * Without this the callback params stay in the address bar, and a refresh replays a
   * one-time authorization code that Keycloak has already consumed - producing an
   * "invalid_grant" the user sees as a random failed login. MSAL handled the equivalent
   * internally via handleRedirectPromise(), which is why main.tsx no longer needs that
   * bootstrap step.
   */
  onSigninCallback: () => {
    window.history.replaceState({}, document.title, window.location.pathname);
  },
};
